import { Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
// import { CircleIcon, SquareIcon, VercelLogoIcon } from '@radix-ui/react-icons';
import {
  CircleIcon,
  FullScreenIcon,
  LineIcon,
  SquareIcon,
  TriangleIcon,
  WhiteboardCircleIcon,
  WhiteboardEllipseIcon,
  WhiteboardRectangleIcon,
  WhiteboardTriangleIcon,
} from '@sketchnote/icons';
import * as React from 'react';
import {
  Panel,
  TDShapeType,
  TDSnapshot,
  TDToolType,
  ToolButton,
  ToolButtonWithTooltip,
  Tooltip,
  useTldrawApp,
} from 'sn-wb-tldraw';

import { ColorMenu, colors } from './ColorMenu';
import { SizeMenu } from './SizeMenu';
interface ShapesMenuProps {
  activeTool: TDToolType;
  isToolLocked: boolean;
}

type ShapeShape = TDShapeType.Ellipse | TDShapeType.Rectangle | TDShapeType.Triangle;

const shapeShapes: ShapeShape[] = [
  TDShapeType.Ellipse,
  TDShapeType.Rectangle,
  TDShapeType.Triangle,
];

const shapeShapeIcons = {
  [TDShapeType.Ellipse]: <WhiteboardEllipseIcon />,
  [TDShapeType.Rectangle]: <WhiteboardRectangleIcon />,
  [TDShapeType.Triangle]: <WhiteboardTriangleIcon />,
};

const activeShapeShapeIcons = {
  [TDShapeType.Ellipse]: (fill) => <WhiteboardCircleIcon fill={fill} />,
  [TDShapeType.Rectangle]: (fill) => (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 245 194"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width={245} height={194} rx={4} fill={fill} />
    </svg>
  ),
  [TDShapeType.Triangle]: (fill) => (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 224 194"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M108.536 1.99999C110.076 -0.666672 113.924 -0.666667 115.464 2L222.851 188C224.391 190.667 222.466 194 219.387 194H4.61284C1.53364 194 -0.390852 190.667 1.14875 188L108.536 1.99999Z"
        fill={fill}
      />
    </svg>
  ),
};

const statusSelector = (s: TDSnapshot) => s.appState.status;

const currentStyleSelector = (s: TDSnapshot) => s.appState.currentShapeStyle;

enum Status {
  SpacePanning = 'spacePanning',
}

export const ShapesMenu = React.memo(function ShapesMenu({
  activeTool,
  isToolLocked,
}: ShapesMenuProps) {
  const app = useTldrawApp();

  const status = app.useStore(statusSelector);

  const isSmallScreen = window.innerHeight < 800;

  const offset = isSmallScreen ? 105 : 125;
  const sideOffset = isSmallScreen ? 29 : 41;

  const [lastActiveTool, setLastActiveTool] = React.useState<ShapeShape>(TDShapeType.Ellipse);
  const [isHovered, setHovered] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (shapeShapes.includes(activeTool as ShapeShape) && lastActiveTool !== activeTool) {
      setLastActiveTool(activeTool as ShapeShape);
    }
  }, [activeTool]);

  const selectShapeTool = React.useCallback(() => {
    app.selectTool(lastActiveTool);
  }, [activeTool, app]);

  const handleDoubleClick = React.useCallback(() => {
    app.toggleToolLock();
  }, [app]);

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === ' ') {
      if (app.shiftKey) {
        e.preventDefault();
      }
    }
  }, []);

  const isActive = shapeShapes.includes(activeTool as ShapeShape);

  const currentShapeStyle = app.useStore(currentStyleSelector);

  return (
    <Menu direction="ltr" isLazy offset={[offset, sideOffset]} placement="right">
      <div
        className={`overflow-hidden h-62 w-80 smallScreen:h-48 smallScreen:w-48 flex justify-center hover:bg-gray-199 relative ${
          isActive && 'bg-gray-199'
        }`}
        style={{ borderBottom: '1px solid #F6F7F9' }}
      >
        <Tooltip
          id="ShapeMenu"
          label={lastActiveTool[0].toUpperCase() + lastActiveTool.slice(1)}
          side="right"
        >
          <MenuButton
            disabled={isActive && app.shiftKey} // otherwise this continuously opens and closes on "SpacePanning"
            onDoubleClick={handleDoubleClick}
            className={`mx-auto py-4 translate-y-10 smallScreen:translate-y-3 scale-135 smallScreen:scale-100 flex justify-center transform ${
              isActive && '-translate-x-20 smallScreen:-translate-x-14'
            } hover:-translate-x-20 smallScreen:hover:-translate-x-14 duration-100 ease-in-out`}
            // isToolLocked={isActive && isToolLocked}
            // isActive={isActive}
            onClick={() => app.selectTool(lastActiveTool)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onKeyDown={handleKeyDown}
          >
            {activeShapeShapeIcons[lastActiveTool](
              colors[currentShapeStyle.color] || colors.yellow
            )}
          </MenuButton>
        </Tooltip>
        <div
          className={`absolute right-14 smallScreen:right-5  bottom-0 opacity-0 translate-x-0 transition-all duration-100 ease-in-out ${
            isHovered ? 'transform opacity-100  translate-x-4' : ''
          }`}
        >
          <FullScreenIcon />
        </div>
      </div>

      <MenuList>
        <Panel
          side="center"
          style={{
            padding: '5px 0',
            boxShadow: '0px 4px 4px rgba(135, 138, 146, 0.15)',
            border: '1px solid #E2E4E9',
            borderRadius: `${isSmallScreen ? '9px' : '14px'}`,
          }}
          className="w-57 smallScreen:w-37"
        >
          <div className="flex flex-col gap-2 justify-center items-center">
            {shapeShapes.map((shape, i) => (
              <div
                key={shape}
                className="overflow-hidden  h-38 w-44 smallScreen:h-25 smallScreen:w-29  flex justify-center items-center  hover:bg-gray-199"
              >
                <Tooltip
                  key={shape}
                  label={shape[0].toUpperCase() + shape.slice(1)}
                  // kbd={(4 + i).toString()}
                  side="right"
                  id={`TD-PrimaryTools-Shapes-${shape}`}
                >
                  <MenuItem>
                    <div className="flex  justify-center items-center transform smallScreen:scale-90 scale-135">
                      <ToolButton
                        variant="primary"
                        onClick={() => {
                          app.selectTool(shape);
                          setLastActiveTool(shape);
                        }}
                      >
                        {shapeShapeIcons[shape]}
                      </ToolButton>
                    </div>
                  </MenuItem>
                </Tooltip>
              </div>
            ))}
          </div>
          {/* Shapes Color Menu*/}
          <div
            className="w-28 mt-7 mb-5 mx-auto"
            style={{ borderBottom: '1px dashed #E2E4E9' }}
          ></div>
          <ColorMenu />
          <div
            className="w-28 mt-9 mb-7 smallScreen:mt-7 smallScreen:mb-5 mx-auto"
            style={{ borderBottom: '1px dashed #E2E4E9' }}
          ></div>
          {/* Shape Size Menu */}
          <SizeMenu />
        </Panel>
      </MenuList>
    </Menu>
  );
});
